<template>
    <canvas ref="canvas" />
</template>
<script>
import { useResize } from '../../assets/js/vue/utlis/useResize';
import { useRafFps } from '../../assets/js/vue/utlis/useRafFps';
import { useIntersectionObserver } from '../../assets/js/vue/utlis/useIntersectionObserver';
import {
    ref, onMounted, onBeforeUnmount,
    computed, watch,
} from 'vue';
import { easeInOutCubic } from '../../assets/js/vue/utlis/easing/easeInOutCubic';
import { useStore } from '../../store';

export default {
    name: 'PrivateCardBg',
    props: {
        lights: {
            type: Array,
            default: () => [],
        },
    },
    setup(props, { emit }) {
        const width = 1920;
        const height = 1293;
        const width_mob = 360;
        const height_mob = 733;
        let ctx = undefined;
        const canvas = ref(null);
        const background = new Image();
        const lightOne = new Image();
        let track1 = 0;
        let track2 = ref(0);
        const speed1 = 10; // loop animation ~time in sec
        const speed2 = 2; // planet animation ~time in sec
        const fps = 60;

        const store = useStore();

        const isMobile = computed(() => store.state.app.isMobile);
        const innerWidth = computed(() => store.state.app.windowWidth);

        const lights = props.lights.map((item) => ({
            deg: item.deg,
            image: new Image(),
            src: item.src ?? '/local/templates/redesign/src/assets/img/private/card/lights.webp',
        }));

        const targetIsVisible = ref(false);
        useIntersectionObserver(
            canvas,
            ([{ isIntersecting }]) => {
                targetIsVisible.value = isIntersecting
                if (isIntersecting) track2.value = 0;
                emit('in-viewport', isIntersecting);
            },
        );

        const updateSize = () => {
            const realWidth = isMobile.value ? width_mob : width;
            const realHeight = isMobile.value ? height_mob : height;
            ctx.canvas.width = innerWidth.value;
            ctx.canvas.height = realHeight*innerWidth.value/realWidth;
        };

        const updateBg = () => {
            const ratio = background.naturalWidth / background.naturalHeight;
            const width = canvas.value.width;
            const height = width / ratio;
            ctx.drawImage(background, 0, 0, width, height);
        };

        const drawLightOne = () => {
            const deg = 360 * track1;
            const r = 555;
            const x0 = 972;
            const y0 = 730;
            const scale = canvas.value.width / 1920;
            const ratio = lightOne.naturalWidth / lightOne.naturalHeight;
            const width = 9 * scale;
            const height = width / ratio;
            const x = (Math.cos(deg*Math.PI/180) * r + x0)*scale;
            const y = (Math.sin(-deg*Math.PI/180) * r + y0)*scale;

            ctx.save();
            if (deg > 0 && deg <= 45) {
                ctx.globalAlpha = deg/45;
            } else if (deg > 45 && deg <= 180) {
                ctx.globalAlpha = 1;
            } else if (deg > 180 && deg <= 225) {
                ctx.globalAlpha = -deg/45 + 5;
            } else {
                ctx.globalAlpha = 0;
            }

            ctx.drawImage(lightOne, x, y, width, height);
            ctx.restore();
        };

        const drawLights = () => {
            lights.forEach((item) => {
                const deg = item.deg * easeInOutCubic(track2.value) + 90;
                const r = 337;
                const x0 = 940;
                const y0 = 685;
                const scale = canvas.value.width / 1920;
                const ratio = item.image.naturalWidth / item.image.naturalHeight;
                const width = 73 * scale;
                const height = width / ratio;
                const x = (Math.cos(deg*Math.PI/180) * r + x0)*scale;
                const y = (Math.sin(-deg*Math.PI/180) * r + y0)*scale;

                ctx.drawImage(item.image, x, y, width, height);
            });
        };

        const draw = () => {
            ctx.clearRect(0, 0, canvas.value.width, canvas.value.height);
            updateBg();
            if (!isMobile.value) drawLightOne();
            if (!isMobile.value) drawLights();

            if (track1 < 1) track1 += 1/speed1/fps; else track1 = 0;
            if (track2.value < 1) track2.value += 1/speed2/fps;
        };

        const init = () => {
            ctx = canvas.value.getContext('2d');
            ctx.imageSmoothingQuality = 'high';

            background.src = isMobile.value ? '/local/templates/redesign/src/assets/img/private/card/private-card-bg_mob.webp' : '/local/templates/redesign/src/assets/img/private/card/private-card-bg.webp';
            lightOne.src = '/local/templates/redesign/src/assets/img/private/card/light.webp';

            lights.forEach((image) => {
                image.image.src = image.src;
            });
        };

        onMounted(() => {
            init();
        });

        watch(isMobile, (val) => {
            background.src = val ? '/local/templates/redesign/src/assets/img/private/card/private-card-bg_mob.webp' : '/local/templates/redesign/src/assets/img/private/card/private-card-bg.webp';
        });

        useResize(() => {
            updateSize();
            draw();
        }, true);

        const pause = computed(() => !targetIsVisible.value);
        useRafFps(() => {
            draw();
        }, pause, fps);

        onBeforeUnmount(() => {
            ctx.value = undefined;
        });

        return {
            ctx,
            canvas,
            track2,
            targetIsVisible,
        };
    },
};
</script>
<style lang="scss">
@import '../../assets/scss/base/variables';
@import '../../assets/scss/base/mixins';
@import '../../assets/scss/base/functions';

.private-card-bg {
    @include not-mobile {
        @include fluid-1920('height', 1293px);
        @include fluid-1920('wdith', 1920px);
    }

    background-position: top center;
    background-repeat: no-repeat;
    background-size: cover;

    @include not-mobile {
        background-image: url('/local/templates/redesign/src/assets/img/private/card/private-card-bg.webp');
    }

    @include mobile {
        background-image: unset;
    }
}
</style>
